import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Modal,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import { v4 as uuidv4 } from "uuid";

import FreshpaintUser from "../../components/users/FreshpaintUser";
import useAccountMetadata from "../../hooks/useAccountMetadata";
import useUsers from "../../hooks/useUsers";
import { makeRequest } from "../../util";
import { SaaSDestination } from "../destinations";
import { ModalField } from "./ConfigureIntegrationModal";
import { useModalStyles } from "./styles";
import useArrayOfDestinations from "../../hooks/useArrayOfDestinations";

// TODO CJ Show additional text to explain that hipaa setting applies to all instances of the destination
const getHipaaFieldForDestinations = (destination: SaaSDestination) => {
  return {
    name: "Disable ID Masking and Enforced Allowlists?",
    key: "hasBAA",
    required: false,
    sensitive: false,
    type: "boolean",
    description: (
      <>
        When this option is checked, data will be sent to {destination.name}{" "}
        without applying Freshpaint&apos;s safeguards for PHI like ID Masking
        and Enforced Allowlists. Some destinations may collect PHI
        automatically, without explicitly receiving events or identify calls.
        You should only disable these features if you have a Business Associate
        Agreement in place with {destination.name}, or if you have determined
        you are not sending Protected Health Information (PHI) to{" "}
        {destination.name}.
        <br />
        <br />
        See the{" "}
        <a
          className="text-brand-red underline"
          href="https://documentation.freshpaint.io/hipaa-mode"
          rel="noopener noreferrer"
          target="_blank"
        >
          HIPAA Mode docs
        </a>{" "}
        for more details.
      </>
    ),
  };
};

export const HIPAADestinationModal = ({
  open,
  onClose,
  onSave,
  dest,
  existingConfig,
}) => {
  const defaultValueMap = {
    hasBAA: false,
  };

  const accountMetadata = useAccountMetadata();

  const usersState = useUsers();

  const classes = useModalStyles();
  const alert = useAlert();

  const [values, setValues] = useState({
    ...defaultValueMap,
    hasBAA: existingConfig.has_baa,
  });

  const handleFieldChange = (key, value) => {
    setValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    const id = uuidv4();

    const defaultConfig = {
      id: id,
      dest_type: dest.key,
      name: dest.key,
      connection_mode: dest.connectionMode,
    };

    const destConfig = {
      ...defaultConfig,
      ...existingConfig,
      // Remove any values that are just a blank string as that's a filler value
      // we use from the backend.
      settings: _.pickBy(existingConfig.settings, (value) => value !== ""),
      has_baa: values.hasBAA,
    };

    makeRequest("update_destination_has_baa", {
      destination_type: dest.key,
      has_baa: values.hasBAA,
    })
      .then(() => {
        // TODO CJ The dest and id are passed to the onSave function but the handler discards them
        onSave && onSave(dest, destConfig.id, destConfig);
        handleClose();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  if (!open) {
    return null;
  }

  if (usersState.loading) {
    return null;
  }

  const currentUser = new FreshpaintUser(usersState.currentUser);

  const hipaaField = getHipaaFieldForDestinations(dest);

  return (
    <Modal onClose={handleClose} open={open}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`HIPAA Settings: ${dest.name}`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <ModalField
            field={hipaaField}
            values={values}
            settings={{}}
            checkboxEnabled={
              !accountMetadata.config.rbac || currentUser.hasAdminAccess()
            }
            handleFieldChange={handleFieldChange}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={handleClose}>Close</Button>
          {onSave && (
            <Button color="primary" onClick={handleSave} variant="contained">
              Save
            </Button>
          )}
        </CardActions>
      </Card>
    </Modal>
  );
};

HIPAADestinationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  dest: PropTypes.object,
  existingConfig: PropTypes.object.isRequired,
};
